import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";

const inputCls =
  "w-full h-12 px-4 rounded-xl border border-zinc-200 bg-white text-base placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 transition-shadow";

const initials = (name = "") =>
  name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

export default function Users() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [users, setUsers] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", emailConfirm: "", password: "", role: "usuario" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/users").then(({ data }) => setUsers(data));
  useEffect(() => { load(); }, []);

  const openDialog = (u = null) => {
    setEditing(u);
    setForm({ name: u?.name || "", email: u?.email || "", emailConfirm: "", password: "", role: u?.role || "usuario" });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editing && form.email.trim().toLowerCase() !== form.emailConfirm.trim().toLowerCase()) {
      toast.error("Los correos electrónicos no coinciden.");
      return;
    }
    if (!editing && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error("Introduce un correo electrónico válido.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await api.patch(`/users/${editing.id}`, payload);
        toast.success("Usuario actualizado");
      } else {
        await api.post("/users", { name: form.name, email: form.email.trim().toLowerCase(), password: form.password, role: form.role });
        toast.success("Usuario creado");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (u) => {
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Usuario eliminado");
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  return (
    <div className="animate-fade-up" data-testid="users-page">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter">Usuarios</h1>
          <p className="text-zinc-500 text-sm mt-1">{user?.company_name}</p>
        </div>
        {isAdmin && (
          <button
            onClick={() => openDialog()}
            data-testid="add-user-button"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 active:scale-95 transition-[background-color,transform] duration-200 shrink-0"
          >
            <Plus size={16} /> <span className="hidden sm:inline">Añadir usuario</span>
          </button>
        )}
      </div>

      {users === null ? (
        <div className="flex justify-center py-20">
          <div className="h-8 w-8 rounded-full border-2 border-zinc-300 border-t-zinc-900 animate-spin" />
        </div>
      ) : (
        <div className="space-y-2.5 max-w-2xl">
          {users.map((u) => (
            <div
              key={u.id}
              data-testid={`user-card-${u.id}`}
              className="flex items-center gap-4 bg-white rounded-2xl border border-zinc-200/80 card-shadow p-4"
            >
              <span className="h-11 w-11 rounded-full bg-zinc-900 text-white flex items-center justify-center text-sm font-semibold shrink-0">
                {initials(u.name)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">
                  {u.name} {u.id === user?.id && <span className="text-zinc-400 font-normal">(tú)</span>}
                </p>
                <p className="text-sm text-zinc-400 truncate">{u.email}</p>
              </div>
              <span
                className={`shrink-0 px-3 py-1 rounded-full text-xs font-medium ${
                  u.role === "admin"
                    ? "bg-zinc-900 text-white"
                    : "bg-zinc-100 text-zinc-600"
                }`}
                data-testid={`user-role-${u.id}`}
              >
                {u.role === "admin" ? "Administrador" : "Usuario"}
              </span>
              {isAdmin && u.id !== user?.id && (
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => openDialog(u)}
                    title="Editar"
                    data-testid={`user-edit-${u.id}`}
                    className="h-9 w-9 rounded-full flex items-center justify-center text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
                  >
                    <Pencil size={15} />
                  </button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        title="Eliminar"
                        data-testid={`user-delete-${u.id}`}
                        className="h-9 w-9 rounded-full flex items-center justify-center text-zinc-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                      >
                        <Trash2 size={15} />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-3xl">
                      <AlertDialogHeader>
                        <AlertDialogTitle>¿Eliminar a {u.name}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Perderá el acceso a la aplicación. Sus intervenciones registradas se conservarán.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-full">Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(u)} className="rounded-full bg-rose-600 hover:bg-rose-700" data-testid="user-delete-confirm">
                          Eliminar
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-3xl sm:max-w-md" data-testid="user-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">
              {editing ? "Editar usuario" : "Añadir usuario"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 pt-2">
            <input
              className={inputCls}
              placeholder="Nombre"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
              data-testid="user-name-input"
            />
            {!editing && (
              <>
                <input
                  className={inputCls}
                  type="text"
                  inputMode="email"
                  autoCapitalize="off"
                  placeholder="Correo electrónico"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  required
                  autoComplete="off"
                  data-testid="user-email-input"
                />
                <input
                  className={inputCls}
                  type="text"
                  inputMode="email"
                  autoCapitalize="off"
                  placeholder="Confirmar correo electrónico"
                  value={form.emailConfirm}
                  onChange={(e) => setForm((f) => ({ ...f, emailConfirm: e.target.value }))}
                  required
                  autoComplete="off"
                  data-testid="user-email-confirm-input"
                />
              </>
            )}
            <div>
              <input
                className={inputCls}
                type="password"
                placeholder={editing ? "Nueva contraseña (opcional)" : "Contraseña para su primer acceso"}
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                required={!editing}
                minLength={form.password ? 6 : 0}
                data-testid="user-password-input"
              />
              <p className="text-xs text-zinc-400 mt-2 leading-relaxed" data-testid="user-password-hint">
                {editing
                  ? "Si la cambias, las sesiones activas de este usuario se cerrarán."
                  : "Mínimo 6 caracteres. El usuario entrará con esta contraseña (comunícasela tú) y podrá cambiarla desde «¿Olvidaste tu contraseña?» en la pantalla de acceso."}
              </p>
            </div>
            <select
              className={inputCls}
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              data-testid="user-role-select"
            >
              <option value="usuario">Usuario — consulta y registra intervenciones</option>
              <option value="admin">Administrador — gestiona todo</option>
            </select>
            <button
              type="submit"
              disabled={saving}
              data-testid="user-submit"
              className="w-full h-12 rounded-full bg-zinc-900 text-white font-medium hover:bg-zinc-800 active:scale-[0.98] transition-[background-color,transform] duration-200 disabled:opacity-50"
            >
              {saving ? "Guardando…" : editing ? "Guardar cambios" : "Crear usuario"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
